/**
 * iTrace (WPK) 前端监控 SDK 初始化与封装
 *
 * 职责：
 * - 初始化 iTrace 单例，配置 bid、plugins、spa 模式
 * - 基础官方插件按名称异步加载，fluency 显式导入并同步初始化
 * - 导出 itrace 实例供其他模块使用
 * - 提供 setItraceUser / setItraceReady 等语义化方法
 */
import { getItraceSingleton } from '@ali/itrace-browser';
import ItraceFluency from '@ali/itrace-fluency';

// ============== 配置 ==============

/**
 * iTrace 平台分配的应用标识。
 */
const ITRACE_BID: string = 'pcr4vtc7-eclk6lhi';

/**
 * 是否为开发环境（开启 debug 日志）。
 * webpack mode 会自动注入 process.env.NODE_ENV。
 */
const IS_DEV = process.env.NODE_ENV !== 'production';

/**
 * 本次构建的版本号，由 webpack DefinePlugin 注入（来源 build.sh：commitId+timestamp）。
 * 用 typeof 守卫规避本地 dev 服务未注入时的 ReferenceError。
 */
const APP_RELEASE: string = typeof __APP_RELEASE__ !== 'undefined' ? __APP_RELEASE__ : '';

// 诊断页面的所有日志共用诊断版本，初始化队列和后续版本更新也保持隔离。
const IS_DIAGNOSTIC =
  IS_DEV ||
  [window.location.search, window.location.hash.split('?')[1]].some((search) =>
    ['1', 'true'].includes(new URLSearchParams(search).get('debug')?.trim().toLowerCase() ?? ''),
  );
const itraceRelease = (rel: string) => (IS_DIAGNOSTIC ? `diagnostic:${rel || 'development'}` : rel);

// ============== SDK 初始化 ==============

const itrace = getItraceSingleton({
  bid: ITRACE_BID,
  spa: true,
  debug: IS_DEV,
  rel: itraceRelease(APP_RELEASE),
  plugins: [
    'jserror',
    'interface',
    'perf',
    'resource',
    'flow',
    'blank',
    {
      id: 'fluency',
      plugin: ItraceFluency,
      // 保留 SDK 原生采样语义，避免二次限流丢失卡顿和正常操作计数。
      sampleRate: 1,
      scrollBlockOpt: { enable: true },
      zoomBlockOpt: { enable: false },
    },
  ],
  beforeSend(log: Record<string, unknown>) {
    // 官方流畅性分类；画布通过 detectBlock 标记 transform 交互，不把 resize 当作画布缩放。
    if ([8, 15, 18].includes(Number(log.category))) {
      delete log.wk_xtext;
    }
    return true;
  },
});

// ============== 公共 API ==============

/**
 * 设置 iTrace SDK 为就绪状态，开始上报日志。
 * 应在应用入口（src/index.tsx）尽早调用。
 */
export function setItraceReady(): void {
  itrace.setReady();
}

/**
 * 设置用户标识，通常在用户登录/切换后调用。
 */
export function setItraceUser(uid: string): void {
  itrace.setConfig({ uid });
}

/**
 * 设置前端版本号。
 */
export function setItraceVersion(rel: string): void {
  itrace.setConfig({ rel: itraceRelease(rel) });
}

export { itrace };
